import React, { useState } from 'react';
import './Relatorios.css';
import html2pdf from 'html2pdf.js';
import { 
  professorService, 
  disciplinaService, 
  professorAgendaService, 
  agendaProfessorService,
  periodoService,
  ProfessorDisciplina,
  Agenda,
  Periodo
} from '../services/api';

interface RelatorioData {
  professor: string;
  quantidadeAulasPorPeriodo: { [periodoNome: string]: number };
  disponibilidadePorPeriodo: { [periodoNome: string]: number };
  totalAulas: number;
  totalDisponibilidade: number;
}

const Relatorios: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [relatorioData, setRelatorioData] = useState<RelatorioData[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [showRelatorio, setShowRelatorio] = useState(false);

  const gerarRelatorioHorarioDisponibilidade = async () => {
    setLoading(true);
    try {
      // Buscar períodos cadastrados
      const periodosData = await periodoService.getAll();
      setPeriodos(periodosData);

      // Buscar professores
      const professores = await professorService.getAll();
      
      const relatorio: RelatorioData[] = [];

      for (const professor of professores) {
        if (!professor.id) continue;

        const professorData: RelatorioData = {
          professor: professor.nome,
          quantidadeAulasPorPeriodo: {},
          disponibilidadePorPeriodo: {},
          totalAulas: 0,
          totalDisponibilidade: 0
        };

        // Inicializar contadores para todos os períodos
        periodosData.forEach(periodo => {
          professorData.quantidadeAulasPorPeriodo[periodo.nome] = 0;
          professorData.disponibilidadePorPeriodo[periodo.nome] = 0;
        });

        try {
          // Buscar disciplinas do professor
          const disciplinasData: ProfessorDisciplina = await disciplinaService.getByProfessor(professor.id!);
          
          // Contar aulas por período
          if (disciplinasData.disciplinas) {
            disciplinasData.disciplinas.forEach(disciplina => {
              const periodoNome = disciplina.turma.periodo.nome;
              const cargaHoraria = disciplina.cargaHoraria || 0;
              if (professorData.quantidadeAulasPorPeriodo[periodoNome] !== undefined) {
                professorData.quantidadeAulasPorPeriodo[periodoNome] += cargaHoraria;
                professorData.totalAulas += cargaHoraria;
              }
            });
          }

          // Buscar agendas do professor
          const agendas: Agenda[] = await professorAgendaService.getAgendasByProfessor(professor.id!);
          
          // Contar disponibilidade por período
          for (const agenda of agendas) {
            if (agenda.id && agenda.periodo) {
              try {
                const horariosAgenda = await agendaProfessorService.getByAgenda(agenda.id.toString());
                
                const disponiveisNoPeriodo = horariosAgenda.filter(horarioAgenda => 
                  horarioAgenda.disponivel
                ).length;

                const periodoNome = agenda.periodo.nome;
                if (professorData.disponibilidadePorPeriodo[periodoNome] !== undefined) {
                  professorData.disponibilidadePorPeriodo[periodoNome] += disponiveisNoPeriodo;
                  professorData.totalDisponibilidade += disponiveisNoPeriodo;
                }
              } catch (error) {
                console.error(`Erro ao buscar horários da agenda ${agenda.id}:`, error);
              }
            }
          }
        } catch (error) {
          console.error(`Erro ao processar dados do professor ${professor.nome}:`, error);
        }

        relatorio.push(professorData);
      }

      setRelatorioData(relatorio);
      setShowRelatorio(true);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const voltarParaRelatorios = () => {
    setShowRelatorio(false);
    setRelatorioData([]);
  };

  // Calcular totais por período
  const calcularTotaisPorPeriodo = () => {
    const totaisPorPeriodo: { [periodoNome: string]: { aulas: number; disponibilidade: number } } = {};
    
    periodos.forEach(periodo => {
      totaisPorPeriodo[periodo.nome] = { aulas: 0, disponibilidade: 0 };
    });

    relatorioData.forEach(item => {
      periodos.forEach(periodo => {
        totaisPorPeriodo[periodo.nome].aulas += item.quantidadeAulasPorPeriodo[periodo.nome] || 0;
        totaisPorPeriodo[periodo.nome].disponibilidade += item.disponibilidadePorPeriodo[periodo.nome] || 0;
      });
    });

    return totaisPorPeriodo;
  };

  // Calcular totais gerais
  const calcularTotaisGerais = () => {
    return relatorioData.reduce(
      (acc, item) => ({
        aulas: acc.aulas + item.totalAulas,
        disponibilidade: acc.disponibilidade + item.totalDisponibilidade
      }),
      { aulas: 0, disponibilidade: 0 }
    );
  };

  const imprimirPDF = () => {
    // Criar elemento temporário com layout profissional para PDF
    const element = document.createElement('div');
    element.innerHTML = gerarLayoutPDF();
    
    // Configurações do PDF
    const opt = {
      margin: 0.5,
      filename: `relatorio-horario-disponibilidade-${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'landscape' as const
      }
    };

    // Gerar PDF
    html2pdf().set(opt).from(element).save();
  };

  const gerarLayoutPDF = () => {
    const totaisPorPeriodo = calcularTotaisPorPeriodo();
    const totaisGerais = calcularTotaisGerais();
    const dataAtual = new Date().toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Gerar cabeçalhos dos períodos para aulas
    const headerAulas = periodos.map(periodo => 
      `<th style="padding: 10px 8px; text-align: center; border-right: 1px solid #5a6c7d; font-weight: 500; font-size: 11px;">${periodo.nome}</th>`
    ).join('');

    // Gerar cabeçalhos dos períodos para disponibilidade
    const headerDisponibilidade = periodos.map(periodo => 
      `<th style="padding: 10px 8px; text-align: center; border-right: 1px solid #5a6c7d; font-weight: 500; font-size: 11px;">${periodo.nome}</th>`
    ).join('');

    // Gerar linhas da tabela
    const linhasTabela = relatorioData.map((item, index) => {
      const aulasRow = periodos.map(periodo => 
        `<td style="padding: 12px 8px; text-align: center; border-right: 1px solid #e0e0e0; color: #e74c3c; font-weight: 500;">${item.quantidadeAulasPorPeriodo[periodo.nome] || 0}</td>`
      ).join('');
      
      const disponibilidadeRow = periodos.map(periodo => 
        `<td style="padding: 12px 8px; text-align: center; border-right: 1px solid #e0e0e0; color: #27ae60; font-weight: 500;">${item.disponibilidadePorPeriodo[periodo.nome] || 0}</td>`
      ).join('');

      return `<tr style="background: ${index % 2 === 0 ? '#f8f9fa' : 'white'}; border-bottom: 1px solid #e0e0e0;">
        <td style="padding: 12px 10px; border-right: 1px solid #e0e0e0; font-weight: 500; color: #2c3e50;">${item.professor}</td>
        ${aulasRow}
        ${disponibilidadeRow}
        <td style="padding: 12px 8px; text-align: center; border-right: 1px solid #e0e0e0; background: #ffe6e6; color: #c0392b; font-weight: bold;">${item.totalAulas}</td>
        <td style="padding: 12px 8px; text-align: center; background: #e8f5e8; color: #27ae60; font-weight: bold;">${item.totalDisponibilidade}</td>
      </tr>`;
    }).join('');

    // Gerar linha de totais
    const totaisAulasRow = periodos.map(periodo => 
      `<td style="padding: 15px 8px; text-align: center; border-right: 1px solid #4a6741;">${totaisPorPeriodo[periodo.nome]?.aulas || 0}</td>`
    ).join('');
    
    const totaisDisponibilidadeRow = periodos.map(periodo => 
      `<td style="padding: 15px 8px; text-align: center; border-right: 1px solid #4a6741;">${totaisPorPeriodo[periodo.nome]?.disponibilidade || 0}</td>`
    ).join('');

    return `<div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; background: white;">
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2c3e50; padding-bottom: 20px;">
        <h1 style="color: #2c3e50; margin: 0; font-size: 28px; font-weight: bold;">Sistema de Gestão Acadêmica</h1>
        <h2 style="color: #34495e; margin: 10px 0 0 0; font-size: 22px; font-weight: 500;">Relatório de Horário x Disponibilidade</h2>
        <p style="color: #7f8c8d; margin: 10px 0 0 0; font-size: 14px;">Gerado em: ${dataAtual}</p>
      </div>
      <div style="display: flex; justify-content: space-around; margin-bottom: 30px; gap: 20px;">
        <div style="background: linear-gradient(135deg, #3498db, #2980b9); color: white; padding: 20px; border-radius: 10px; text-align: center; flex: 1;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px;">Total de Professores</h3>
          <p style="margin: 0; font-size: 32px; font-weight: bold;">${relatorioData.length}</p>
        </div>
        <div style="background: linear-gradient(135deg, #e74c3c, #c0392b); color: white; padding: 20px; border-radius: 10px; text-align: center; flex: 1;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px;">Total de Aulas</h3>
          <p style="margin: 0; font-size: 32px; font-weight: bold;">${totaisGerais.aulas}</p>
        </div>
        <div style="background: linear-gradient(135deg, #27ae60, #229954); color: white; padding: 20px; border-radius: 10px; text-align: center; flex: 1;">
          <h3 style="margin: 0 0 10px 0; font-size: 16px;">Total Disponibilidade</h3>
          <p style="margin: 0; font-size: 32px; font-weight: bold;">${totaisGerais.disponibilidade}</p>
        </div>
      </div>
      <div style="background: white; border-radius: 10px; overflow: hidden; border: 1px solid #e0e0e0;">
        <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
          <thead>
            <tr style="background: linear-gradient(135deg, #34495e, #2c3e50); color: white;">
              <th rowspan="2" style="padding: 15px 10px; text-align: left; border-right: 1px solid #4a6741; font-weight: 600;">Professor</th>
              <th colspan="${periodos.length}" style="padding: 15px 10px; text-align: center; border-right: 1px solid #4a6741; font-weight: 600;">Quantidade de Aulas</th>
              <th colspan="${periodos.length}" style="padding: 15px 10px; text-align: center; border-right: 1px solid #4a6741; font-weight: 600;">Disponibilidade</th>
              <th rowspan="2" style="padding: 15px 10px; text-align: center; border-right: 1px solid #4a6741; font-weight: 600;">Total Aulas</th>
              <th rowspan="2" style="padding: 15px 10px; text-align: center; font-weight: 600;">Total Disponibilidade</th>
            </tr>
            <tr style="background: #455a64; color: white;">
              ${headerAulas}
              ${headerDisponibilidade}
            </tr>
          </thead>
          <tbody>
            ${linhasTabela}
          </tbody>
          <tfoot>
            <tr style="background: linear-gradient(135deg, #2c3e50, #34495e); color: white; font-weight: bold;">
              <td style="padding: 15px 10px; border-right: 1px solid #4a6741;">TOTAIS</td>
              ${totaisAulasRow}
              ${totaisDisponibilidadeRow}
              <td style="padding: 15px 8px; text-align: center; border-right: 1px solid #4a6741; background: rgba(231, 76, 60, 0.2);">${totaisGerais.aulas}</td>
              <td style="padding: 15px 8px; text-align: center; background: rgba(39, 174, 96, 0.2);">${totaisGerais.disponibilidade}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #ecf0f1; text-align: center; color: #7f8c8d; font-size: 12px;">
        <p style="margin: 0;">Sistema de Gestão Acadêmica - Relatório gerado automaticamente</p>
        <p style="margin: 5px 0 0 0;">© ${new Date().getFullYear()} - Todos os direitos reservados</p>
      </div>
    </div>`;
  };

  if (showRelatorio) {
    const totaisPorPeriodo = calcularTotaisPorPeriodo();
    const totaisGerais = calcularTotaisGerais();

    return (
      <div className="relatorios-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button 
            className="btn btn-outline-secondary"
            onClick={voltarParaRelatorios}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Voltar
          </button>
          <button 
            className="btn btn-primary"
            onClick={imprimirPDF}
          >
            <i className="fas fa-print me-2"></i>
            Imprimir PDF
          </button>
        </div>
        
        <div className="relatorios-header">
          <h2 className="mb-4">
            <i className="fas fa-chart-bar me-2"></i>
            Relatório: Horário x Disponibilidade
          </h2>
        </div>

        <div className="relatorio-stats mb-4">
          <div className="row">
            <div className="col-md-6">
              <div className="stat-card stat-card-primary">
                <div className="stat-icon">
                  <i className="fas fa-chalkboard-teacher"></i>
                </div>
                <div className="stat-content">
                  <h3>{totaisGerais.aulas}</h3>
                  <p>Total de Aulas</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="stat-card stat-card-success">
                <div className="stat-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="stat-content">
                  <h3>{totaisGerais.disponibilidade}</h3>
                  <p>Total de Disponibilidade</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card modern-card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-table me-2"></i>
              Detalhamento por Professor e Período
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-modern mb-0">
                <thead>
                  <tr>
                    <th rowSpan={2} className="align-middle professor-column">
                      <i className="fas fa-user me-2"></i>
                      Professor
                    </th>
                    <th colSpan={periodos.length} className="text-center section-header aulas-header">
                      <i className="fas fa-book me-2"></i>
                      Quantidade de Aulas
                    </th>
                    <th colSpan={periodos.length} className="text-center section-header disponibilidade-header">
                      <i className="fas fa-calendar-check me-2"></i>
                      Disponibilidade
                    </th>
                    <th rowSpan={2} className="align-middle total-column total-aulas">
                      <i className="fas fa-sum me-2"></i>
                      Total Aulas
                    </th>
                    <th rowSpan={2} className="align-middle total-column total-disponibilidade">
                      <i className="fas fa-sum me-2"></i>
                      Total Disponib.
                    </th>
                  </tr>
                  <tr>
                    {periodos.map(periodo => (
                      <th key={`aulas-${periodo.id}`} className="text-center periodo-header">
                        {periodo.nome}
                      </th>
                    ))}
                    {periodos.map(periodo => (
                      <th key={`disp-${periodo.id}`} className="text-center periodo-header">
                        {periodo.nome}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {relatorioData.map((item, index) => (
                    <tr key={index} className="data-row">
                      <td className="professor-name">
                        <div className="professor-info">
                          <i className="fas fa-user-circle me-2"></i>
                          {item.professor}
                        </div>
                      </td>
                      {periodos.map(periodo => (
                        <td key={`aulas-${periodo.id}-${index}`} className="text-center data-cell aulas-cell">
                          <span className="data-value">
                            {item.quantidadeAulasPorPeriodo[periodo.nome] || 0}
                          </span>
                        </td>
                      ))}
                      {periodos.map(periodo => (
                        <td key={`disp-${periodo.id}-${index}`} className="text-center data-cell disponibilidade-cell">
                          <span className="data-value">
                            {item.disponibilidadePorPeriodo[periodo.nome] || 0}
                          </span>
                        </td>
                      ))}
                      <td className="text-center total-cell total-aulas-cell">
                        <span className="total-value">
                          {item.totalAulas}
                        </span>
                      </td>
                      <td className="text-center total-cell total-disponibilidade-cell">
                        <span className="total-value">
                          {item.totalDisponibilidade}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td className="totals-label">
                      <strong>
                        <i className="fas fa-calculator me-2"></i>
                        TOTAIS POR PERÍODO
                      </strong>
                    </td>
                    {periodos.map(periodo => (
                      <td key={`total-aulas-${periodo.id}`} className="text-center totals-cell aulas-total">
                        <strong>{totaisPorPeriodo[periodo.nome].aulas}</strong>
                      </td>
                    ))}
                    {periodos.map(periodo => (
                      <td key={`total-disp-${periodo.id}`} className="text-center totals-cell disponibilidade-total">
                        <strong>{totaisPorPeriodo[periodo.nome].disponibilidade}</strong>
                      </td>
                    ))}
                    <td className="text-center totals-cell grand-total aulas-grand-total">
                      <strong>{totaisGerais.aulas}</strong>
                    </td>
                    <td className="text-center totals-cell grand-total disponibilidade-grand-total">
                      <strong>{totaisGerais.disponibilidade}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {relatorioData.length === 0 && (
              <div className="text-center py-5">
                <i className="fas fa-info-circle text-muted me-2 fa-2x"></i>
                <h5 className="text-muted mt-3">Nenhum dado encontrado</h5>
                <p className="text-muted">Não foram encontrados dados para gerar o relatório.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relatorios-container">
      <div className="relatorios-header">
        <h2 className="mb-4">
          <i className="fas fa-chart-line me-2"></i>
          Relatórios
        </h2>
        <p className="text-muted mb-4">
          Selecione um relatório para visualizar as informações detalhadas do sistema.
        </p>
      </div>

      <div className="relatorios-grid">
        <div className="card relatorio-card">
          <div className="card-body text-center">
            <div className="relatorio-icon mb-3">
              <i className="fas fa-clock fa-3x text-primary"></i>
            </div>
            <h5 className="card-title">Horário x Disponibilidade</h5>
            <p className="card-text text-muted">
              Visualize a quantidade de aulas e disponibilidade de horários por professor, 
              organizados por período cadastrado no sistema.
            </p>
            <button 
              className="btn btn-success btn-lg"
              onClick={gerarRelatorioHorarioDisponibilidade}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Gerando...
                </>
              ) : (
                <>
                  <i className="fas fa-chart-bar me-2"></i>
                  Gerar Relatório
                </>
              )}
            </button>
          </div>
        </div>

        {/* Placeholder para futuros relatórios */}
        <div className="card relatorio-card">
          <div className="card-body text-center">
            <div className="relatorio-icon mb-3">
              <i className="fas fa-users fa-3x text-secondary"></i>
            </div>
            <h5 className="card-title text-muted">Relatório de Professores</h5>
            <p className="card-text text-muted">
              Em breve: Relatório detalhado sobre professores e suas disciplinas.
            </p>
            <button className="btn btn-outline-secondary btn-lg" disabled>
              <i className="fas fa-hourglass-half me-2"></i>
              Em Desenvolvimento
            </button>
          </div>
        </div>

        <div className="card relatorio-card">
          <div className="card-body text-center">
            <div className="relatorio-icon mb-3">
              <i className="fas fa-calendar-alt fa-3x text-secondary"></i>
            </div>
            <h5 className="card-title text-muted">Relatório de Grades</h5>
            <p className="card-text text-muted">
              Em breve: Análise completa das grades de horários e ocupação.
            </p>
            <button className="btn btn-outline-secondary btn-lg" disabled>
              <i className="fas fa-hourglass-half me-2"></i>
              Em Desenvolvimento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;